import loaderIcon from '@/images/icons/loader-icon.svg';
import { getCurrentStateByUser } from '@/utils/api/habitacionalService';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useGigyaService } from './useGigyaService';

const GIGYA_APIKEY = process.env.GATSBY_GIGYA_APIKEY ?? '';
const ORIGIN_URL = process.env.GATSBY_ORIGIN_URL ?? 'https://qa.comfama.com';

// Variable a nivel de módulo: persiste aunque AuthProvider se remonté durante la navegación
let _loginAttempted = false;

/** Solo para tests: permite resetear el flag entre casos de prueba */
export const __resetLoginAttempted = () => {
    _loginAttempted = false;
};

interface AuthContextProps {
    gigyaAccount: any;
    isLoadingGigya: boolean;
    login: () => void;
    logout: () => void;
    userId: string | null;
    isReady: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);
const GIGYA_DOMAIN = process.env.GATSBY_GIGYA_DOMAIN ?? '';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { account, isLoading, login, logout } = useGigyaService({
        apiKey: GIGYA_APIKEY,
        apiDomain: GIGYA_DOMAIN,
        enableDebugMode: process.env.NODE_ENV === 'development',
    });

    const [userId, setUserId] = useState<string | null>(null);
    const [isReady, setIsReady] = useState(false);

    const handleLogout = async () => {
        localStorage.removeItem('FamilyUserID');
        await logout();
        window.location.href = 'https://www.comfama.com/';
    };

    useEffect(() => {
        if (isLoading) return;

        if (account?.data?.docNumber) {
            _loginAttempted = true;
            const docNumber = account.data.docNumber;
            getCurrentStateByUser(docNumber)
                .then((res) => res.json())
                .then((response) => {
                    if (
                        response?.status === 404 ||
                        response?.data?.isLinkedCamc === false
                    ) {
                        window.location.href = `${ORIGIN_URL}/mentoria-financiera/diagnostico-financiero/#/propositos`;
                        return;
                    }
                    const uid = response?.data?.userId;
                    if (uid) {
                        localStorage.setItem('FamilyUserID', String(uid));
                        setUserId(String(uid));
                        setIsReady(true);
                    }
                })
                .catch((err) => console.error('Auth sync error:', err));
        } else if (!_loginAttempted) {
            _loginAttempted = true;
            login();
        }
    }, [account, isLoading]);

    const isFullyAuthenticated = !isLoading && !!account && isReady && !!userId;

    return (
        <AuthContext.Provider
            value={{
                gigyaAccount: account,
                isLoadingGigya: isLoading,
                login,
                logout: handleLogout,
                userId,
                isReady,
            }}
        >
            {!isFullyAuthenticated ? (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff',
                        zIndex: 99998,
                    }}
                >
                    <img
                        src={loaderIcon}
                        alt="Cargando..."
                        style={{
                            width: 60,
                            height: 60,
                            animation: 'spin 1s linear infinite',
                        }}
                    />
                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const getLocalToken = () => {
    if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem('mentoria-token');
    }
    return null;
};
